import { ConfigProvider, theme } from 'antd';
import enUS from 'antd/locale/en_US';
import esES from 'antd/locale/es_ES';
import type { PropsWithChildren } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const localeMap = {
  en: enUS,
  es: esES,
} as const;

export function AntdProvider({ children }: PropsWithChildren) {
  const [isDark, setIsDark] = useState(false);
  const { i18n } = useTranslation();

  const antdLocale = localeMap[i18n.language as keyof typeof localeMap] ?? enUS;

  useEffect(() => {
    const darkMode =
      document.documentElement.classList.contains('dark') ||
      localStorage.getItem('tenant-ui-theme-mode') === 'dark';
    setIsDark(darkMode);

    const observer = new MutationObserver(() => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <ConfigProvider
      locale={antdLocale}
      theme={{
        algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {
          colorPrimary: '#ff850a',
          colorInfo: '#6d35cc',
          colorSuccess: '#10b981',
          colorWarning: '#f59e0b',
          colorError: '#ef4444',
          fontFamily: 'Inter, sans-serif',
          borderRadius: 8,
        },
        components: {
          Layout: {
            headerBg: isDark ? '#2f165f' : '#5a2cab',
            siderBg: isDark ? '#1f2937' : '#ffffff',
            bodyBg: isDark ? '#111827' : '#f9fafb',
          },
          Menu: {
            itemBg: 'transparent',
            itemSelectedBg: isDark ? '#374151' : '#f3f4f6',
            itemHoverBg: isDark ? '#374151' : '#f3f4f6',
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );
}
